import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CabPage from "../CabPage";
import MobileNav from "../account/MobileNav";
import "../css/transports.css";
import "../css/unified.css";
import "../search/search.css";
import SearchHero, { dailySeries } from "../search/SearchHero";
import { FilterTabs, SearchFooter, VIEW_ICONS } from "../search/SearchChrome";
import useCompanies from "../search/useCompanies";
import { Icon, D, VehiclePhoto } from "../search/icons";
import CompanyLogo from "../search/CompanyLogo";
import { Modal } from "../search/ui";
import MapPanel from "../search/MapPanel";
import { truckInfo } from "../search/mapInfo";
import { TruckResultsDialog } from "../search/dialogs";
import SearchFields from "../search/SearchFields";
import TruckRequestPanel from "../search/TruckRequestPanel";
import { TruckDetailsPanel } from "../ui/cards";
import RowMenu, { useHiddenRows, useRowMenu } from "../ui/RowMenu";
import { useSentRequests } from "../search/store";
import {
  EMPTY_FILTERS,
  countWord,
  destCities,
  filterTrucks,
  fmtAgo,
  fmtDuration,
  fmtFree,
  fmtFreeTime,
  knownCities,
  nf,
  pushRecent,
  sortTrucks,
  truckText,
  useLiveTrucks,
  SEARCH_FORBIDDEN_TEXT,
  useMetrics,
} from "../search/liveTrucks";

/**
 * «Найти транспорт» — wireframe iTruck_Transport_Search.html.
 * Same controls and behaviours as the wireframe script: tabs «Параметры поиска» /
 * «Дополнительные фильтры» / «Сохранённые поиски» (dialog), «Сохранить поиск», views
 * «На карте» / «Списком», expandable map with zoom / centre, sort, select-all,
 * «Отправить заявку» (dialog «Заявка — …»), `···` (dialog with «В избранное»), ⌘K results.
 */
const PAGE = 50;
const key = (t) => `${t.driver_id}-${t.vehicle_id}`;
const routeText = (f) => `${f.origin || "Любой город"} → ${f.destination || "Любое направление"}`;
const machines = (n) => countWord(n, ["машина", "машины", "машин"]);

export default function TransportsPage() {
  const { trucks, loading, error, forbidden, reload } = useLiveTrucks();
  const role = useSelector((s) => s.auth.user?.role);
  const userId = useSelector((s) => s.auth.user?.id);
  const rowMenu = useRowMenu();
  const hid = useHiddenRows("trucks", userId);
  const metrics = useMetrics(role);
  const sent = useSentRequests();
  const { byOwner, byName, all: companies } = useCompanies();
  const companyOf = useCallback((t) => byOwner[String(t.driver_id)] || byName[String(t.company_name || "").trim().toLowerCase()] || null, [byOwner, byName]);

  const [form, setForm] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [view, setView] = useState("list");
  const [sort, setSort] = useState("relevance");
  const [shown, setShown] = useState(PAGE);
  const [dialog, setDialog] = useState(null);
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);
  const resultsRef = useRef(null);

  const say = useCallback((msg) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(""), 4200);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // wireframe search(): filter, uncheck select-all, toast the count
  const search = (f) => {
    const n = filterTrucks(trucks, f).length;
    setForm(f);
    setApplied(f);
    setShown(PAGE);
    pushRecent(f);
    say(`Показано ${n} ${machines(n)}`);
  };
  const onSubmit = (e) => {
    e.preventDefault();
    search(form);
  };
  const reset = () => search(EMPTY_FILTERS); // wireframe reset(): defaults + search()

  const rows = useMemo(
    () => sortTrucks(filterTrucks(trucks, applied), sort).filter((t) => !hid.isHidden(t.vehicle_id)),
    [trucks, applied, sort, hid.count, companyOf], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const cities = useMemo(() => knownCities(trucks), [trucks]);
  const visible = rows.slice(0, shown);

  const openMap = () => {
    setView("map");
    setDialog({ type: "map" });
  };
  const showList = () => {
    setView("list");
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const focusParams = () => {
    document.querySelector("#search-form input[name=origin]")?.focus();
  };

  useEffect(() => {
    const onSearch = (e) => {
      const q = String(e.detail?.q || "").trim().toLowerCase();
      if (!q) return;
      e.preventDefault();
      setDialog({ type: "results", matches: trucks.filter((t) => truckText(t).includes(q)) });
    };
    window.addEventListener("cab:global-search", onSearch);
    return () => window.removeEventListener("cab:global-search", onSearch);
  }, [trucks]);


  const allCities = new Set(trucks.map((t) => t.city).filter(Boolean)).size;
  const companySeries = useMemo(() => dailySeries(companies, (c) => c.created_at), [companies]);
  const tiles = [
    { d: D.truck, tone: 0, value: loading ? "…" : forbidden ? "—" : nf(trucks.length), label: "Активных машин" },
    companies.length > 0 && {
      d: D.box,
      tone: 1,
      value: nf(companies.length),
      label: "Компаний",
      series: companySeries,
      seriesHint: "Новых компаний за 7 дней; процент — к предыдущим 7 дням",
    },
    { d: D.people, tone: 2, value: loading ? "…" : forbidden ? "—" : nf(allCities), label: "Городов" },
    metrics?.first_response_time_avg_seconds != null && {
      d: D.clock,
      tone: 3,
      value: fmtDuration(metrics.first_response_time_avg_seconds),
      label: (
        <>
          Среднее время
          <br />
          до первого отклика
        </>
      ),
    },
  ];

  const sentKey = rows.map((t) => (sent.has(t.vehicle_id) ? 1 : 0)).join("");
  const mapPoints = useMemo(
    () =>
      rows.map((t) => ({
        lat: t.lat,
        lng: t.lng,
        city: t.city,
        title: `${t.driver_name} · ${t.mark || ""}`,
        info: truckInfo(t, { sent: sent.has(t.vehicle_id), onRequest: () => setDialog({ type: "request", trucks: [t] }), onDetails: () => setDialog({ type: "details", truck: t }) }),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, sentKey],
  );
  const caption = routeText(applied);
  const mapSummary = (
    <>
      <strong>
        Показано {nf(rows.length)} {machines(rows.length)}
      </strong>
      <small>{caption}</small>
    </>
  );

  return (
    <CabPage name="transports">
      <main id="main">
        <SearchHero mode="transport" tiles={tiles} />

        <div className="loads-top">
          <section className="left-search">
            <div className="search-panel">
              <FilterTabs view={view} onParams={focusParams} onMap={openMap} onList={showList} />
              <form id="search-form" onSubmit={onSubmit}>
                <SearchFields form={form} setForm={setForm} />
                <SearchFooter onReset={reset} submit="Найти транспорт" />
              </form>
            </div>
          </section>

          <MapPanel
            ariaLabel={`Карта маршрута ${caption}`}
            points={mapPoints}
            summary={mapSummary}
            icon={D.truck}
            wrap
            expandLabel="Показать все машины на карте"
            onExpand={openMap}
            onCenter={() => say("Показан весь маршрут")}
          />
        </div>

        <section className="results" ref={resultsRef}>
          <div className="results-heading">
            <div>
              <h2>
                Найдено <span id="result-count">{rows.length}</span> {countWord(rows.length)}
              </h2>
              <p id="route-caption">Свободные машины, которые могут взять ваш груз</p>
              {hid.count ? (
                <button className="hidden-rows" type="button" onClick={hid.showAll}>
                  Показать скрытые ({hid.count})
                </button>
              ) : null}
            </div>
            <div className="views" role="group" aria-label="Вид результатов">
              <button type="button" className={view === "list" ? "active" : ""} onClick={showList}>
                <Icon d={VIEW_ICONS.list} />
                Списком
              </button>
              <button type="button" className={view === "map" ? "active" : ""} onClick={openMap}>
                <Icon d={VIEW_ICONS.map} />
                На карте
              </button>
            </div>
            <label className="sort">
              <span>Сортировка:</span>
              <select id="sort" aria-label="Сортировка" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="relevance">По релевантности</option>
                <option value="rate">По ставке</option>
                <option value="distance">По расстоянию</option>
              </select>
              <Icon d={D.chevron} />
            </label>
          </div>
          <div className="table-container">
            {forbidden && (
              <div className="empty" role="alert">
                <h3>{SEARCH_FORBIDDEN_TEXT}</h3>
                <p>Поиск свободных машин доступен диспетчерам и компаниям.</p>
              </div>
            )}
            {error && !forbidden && (
              <div className="live-note" style={{ padding: "12px 16px" }}>
                Не удалось загрузить машины.{" "}
                <button type="button" style={{ color: "#0063ff" }} onClick={reload}>
                  Повторить
                </button>
              </div>
            )}
            <table>
              <colgroup>
                <col style={{ width: "16%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "18.4%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Перевозчик / Водитель</th>
                  <th>Машина</th>
                  <th>Текущая локация</th>
                  <th>Предпочтительный маршрут</th>
                  <th>Когда свободен</th>
                  <th>Ставка ⓘ</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((t) => {
                  const ds = destCities(t);
                  const isSent = sent.has(t.vehicle_id);
                  return (
                    <tr key={key(t)}>
                      <td data-label="Перевозчик / Водитель">
                        <div className="carrier">
                          <CompanyLogo src={companyOf(t)?.profile_images} />
                          <div>
                            <strong>
                              <span className="name" title={t.company_name || t.driver_name || ""}>{t.company_name || t.driver_name || "—"}</span>
                              {companyOf(t)?.verified ? (
                                <span className="verified" role="img" aria-label="Проверен" title="Проверенная компания">
                                  ✓
                                </span>
                              ) : null}
                            </strong>
                            <small>{t.company_name && t.driver_name ? t.driver_name : t.status === "BUSY" ? "В рейсе" : "Свободен"}</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Машина">
                        <div className="vehicle">
                          <VehiclePhoto label={t.mark || "Машина"} />
                          <div>
                            <strong>{t.mark || "—"}</strong>
                            <small>{[t.body_type_name, t.max_load_weight != null ? `${t.max_load_weight} т` : null].filter(Boolean).join(" • ")}</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Текущая локация">
                        <div className="location-cell">
                          <Icon d={D.pin} />
                          <div>
                            {t.city || "—"}
                            <small>{[t._dist != null ? `${t._dist} км от «Откуда»` : null, fmtAgo(t.confirmed_minutes_ago)].filter(Boolean).join(" · ")}</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Предпочтительный маршрут">
                        <div className="route-cell">
                          <div>
                            {ds.length ? (
                              <>
                                {t.city || "—"} <span className="route-arrow">→</span> {ds.join(", ")}
                              </>
                            ) : (
                              "Любое направление"
                            )}
                            {t.radius_km != null && <small>радиус подачи {t.radius_km} км</small>}
                          </div>
                        </div>
                      </td>
                      <td data-label="Когда свободен">
                        <strong>{fmtFree(t)}</strong>
                        <small>{fmtFreeTime(t)}</small>
                      </td>
                      <td data-label="Ставка">
                        <b>{t.min_price != null ? `от ${Number(t.min_price).toLocaleString("ru-RU")} ${t.currency || ""}`.trim() : "—"}</b>
                      </td>
                      <td data-label="Действия">
                        <div className="row-actions">
                          <button className="button primary request" disabled={isSent} onClick={() => setDialog({ type: "request", trucks: [t] })}>
                            {isSent ? "Заявка отправлена" : "Отправить заявку"}
                          </button>
                          <button className="button more" aria-haspopup="menu" aria-label={`Действия с ${t.driver_name}`} title="Действия с машиной" onClick={(e) => rowMenu.openMenu(e, t)}>
                            ···
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length > shown && (
              <div className="results-more">
                <button className="button" onClick={() => setShown((n) => n + PAGE)}>
                  Показать ещё
                </button>
              </div>
            )}
            {!loading && !forbidden && rows.length === 0 && (
              <div className="empty">
                <h3>Подходящих машин не найдено</h3>
                <p>Измените параметры поиска или сбросьте фильтры.</p>
                <button className="button" onClick={reset}>
                  Сбросить фильтры
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <MobileNav />

      {rowMenu.menu && (
        <RowMenu
          anchor={rowMenu.menu.anchor}
          items={[
            { label: "Открыть детали", onClick: () => setDialog({ type: "details", truck: rowMenu.menu.payload }) },
            { label: "Скрыть из результатов", onClick: () => hid.hide(rowMenu.menu.payload.vehicle_id) },
          ]}
          onClose={rowMenu.closeMenu}
        />
      )}
      {dialog?.type === "request" && (
        <TruckRequestPanel
          trucks={dialog.trucks}
          onClose={() => setDialog(null)}
          onSent={(ids) => {
            sent.add(ids, null);
            say("Заявка отправлена");
          }}
        />
      )}
      {dialog?.type === "details" && (
        <TruckDetailsPanel
          truck={dialog.truck}
          sent={sent.has(dialog.truck.vehicle_id)}
          onRequest={() => setDialog({ type: "request", trucks: [dialog.truck] })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === "results" && <TruckResultsDialog matches={dialog.matches} onClose={() => setDialog(null)} />}
      {dialog?.type === "map" && (
        <Modal title={caption} wide full onClose={() => setDialog(null)}>
          <MapPanel ariaLabel={`Карта маршрута ${caption}`} points={mapPoints} summary={mapSummary} icon={D.truck} onCenter={() => say("Показан весь маршрут")} />
        </Modal>
      )}
      <div id="toast" role="status" aria-live="polite" className={toast ? "visible" : ""}>
        {toast}
      </div>
      <datalist id="city-options">
        {cities.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
    </CabPage>
  );
}
